import { Customer } from './environment';
import { getCookie } from './cookies';
import { getCorrelationIdentifiers } from './correlation';
import { heapTrack, recordCustomEventToNewRelic } from './helpers';

const NEW_RELIC_SESSION_STORAGE_KEY = 'NRBA_SESSION';
const CART_COOKIE_KEY_SEPARATOR = '?';
const SANITIZED_CART_COOKIE_ID_LENGTH = 24;
const CORRELATION_CONTEXT_EVENT_NAME = 'correlation_context_initialized';
const CORRELATION_CONTEXT_EVENT_RETRY_DELAY_MS = 1000;
const CORRELATION_CONTEXT_EVENT_FORCE_RETRY_DELAY_MS = 2000;

let hasScheduledCorrelationContextTelemetry = false;
let hasSentCorrelationContextTelemetryToHeap = false;
let hasSentCorrelationContextTelemetryToNewRelic = false;

export const getNewRelicSessionIdFromLocalStorage = () => {
  try {
    const rawSession = window.localStorage.getItem(NEW_RELIC_SESSION_STORAGE_KEY);
    if (!rawSession) return null;

    const parsedSession: Record<'value', string> = JSON.parse(rawSession);
    return parsedSession?.value?.toString() || null;
  } catch (error) {
    console.log('Failed to read New Relic session id from localStorage:', error);
    return null;
  }
};

const getSanitizedCartCookieId = () => {
  const cartCookieValue = getCookie('cart');
  if (!cartCookieValue) return null;

  const [cartCookieId] = cartCookieValue.split(CART_COOKIE_KEY_SEPARATOR);
  // Cap to the first 24 chars for safety so we only keep the cart id portion and never leak any appended key material.
  return cartCookieId?.slice(0, SANITIZED_CART_COOKIE_ID_LENGTH) || null;
};

const getBuildInfo = () => {
  try {
    return window.buildInfo?.() || null;
  } catch (error) {
    console.log('Failed to read build info from window.buildInfo():', error);
    return null;
  }
};

export const getCorrelationContext = () => {
  const cartCookieId = getSanitizedCartCookieId();
  const { clientInstanceId, clientSessionId } = getCorrelationIdentifiers();
  const buildInfo = getBuildInfo();

  return {
    customerId: Customer?.id || null,
    cartCookieId: cartCookieId || null,
    clientInstanceId,
    clientSessionId,
    heapSessionId: window.heap?.getSessionId?.() || null,
    newRelicSessionId: getNewRelicSessionIdFromLocalStorage(),
    pathname: window.location.pathname,
    shopifyCountry: window.Shopify?.country || null,
    shopifyLocale: window.Shopify?.locale || null,
    shopifyThemeId: window.Shopify?.theme?.id?.toString() || null,
    shopifyThemeName: window.Shopify?.theme?.name || null,
    buildNumber: buildInfo?.build || null,
    buildDate: buildInfo?.date || null,
    buildSha: buildInfo?.sha || null,
    buildRef: buildInfo?.ref || null,
  };
};

const getNewRelicAgent = () => window.newrelic ?? window.NREUM;

const getCorrelationContextTelemetryPayload = () =>
  Object.fromEntries(Object.entries(getCorrelationContext()).filter(([, value]) => value !== null));

const shouldSendCorrelationContextTelemetryToHeap = (force: boolean) => {
  if (hasSentCorrelationContextTelemetryToHeap || typeof window.heap?.track !== 'function') return false;
  return force || Boolean(window.heap?.getSessionId?.());
};

const shouldSendCorrelationContextTelemetryToNewRelic = (force: boolean) => {
  if (hasSentCorrelationContextTelemetryToNewRelic || typeof getNewRelicAgent()?.recordCustomEvent !== 'function')
    return false;

  return force || Boolean(getNewRelicSessionIdFromLocalStorage());
};

const sendCorrelationContextTelemetry = (force = false) => {
  const payload = getCorrelationContextTelemetryPayload();

  if (shouldSendCorrelationContextTelemetryToNewRelic(force)) {
    recordCustomEventToNewRelic(CORRELATION_CONTEXT_EVENT_NAME, payload);
    hasSentCorrelationContextTelemetryToNewRelic = true;
  }

  if (shouldSendCorrelationContextTelemetryToHeap(force)) {
    heapTrack(CORRELATION_CONTEXT_EVENT_NAME, payload);
    hasSentCorrelationContextTelemetryToHeap = true;
  }

  return hasSentCorrelationContextTelemetryToHeap && hasSentCorrelationContextTelemetryToNewRelic;
};

export const scheduleCorrelationContextTelemetry = () => {
  if (hasScheduledCorrelationContextTelemetry) return;
  hasScheduledCorrelationContextTelemetry = true;

  const hasSentTelemetryToAllTargets = sendCorrelationContextTelemetry();
  if (hasSentTelemetryToAllTargets) return;

  window.setTimeout(() => {
    const hasSentTelemetryToAllTargetsAfterRetry = sendCorrelationContextTelemetry();
    if (hasSentTelemetryToAllTargetsAfterRetry) return;

    window.setTimeout(() => {
      sendCorrelationContextTelemetry(true);
    }, CORRELATION_CONTEXT_EVENT_FORCE_RETRY_DELAY_MS - CORRELATION_CONTEXT_EVENT_RETRY_DELAY_MS);
  }, CORRELATION_CONTEXT_EVENT_RETRY_DELAY_MS);
};
